import { useState, useCallback, useRef, useEffect } from 'react'
import styles from './ColorPicker.module.css'

export interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
  presets?: string[]
  label?: string
  className?: string
}

const DEFAULT_PRESETS = [
  '#7c3aed', // purple
  '#ec4899', // pink
  '#0ea5e9', // blue
  '#059669', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
]

export function ColorPicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  label,
  className,
}: ColorPickerProps) {
  const [inputValue, setInputValue] = useState(value)
  const nativeRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleNativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const color = e.target.value
      setInputValue(color)
      onChange(color)
    },
    [onChange],
  )

  const handleHexInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let hex = e.target.value
      // Ensure # prefix
      if (hex && !hex.startsWith('#')) {
        hex = '#' + hex
      }
      setInputValue(hex)

      // Only fire onChange for valid hex colors
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) {
        onChange(hex)
      }
    },
    [onChange],
  )

  const handlePresetClick = useCallback(
    (color: string) => {
      setInputValue(color)
      onChange(color)
    },
    [onChange],
  )

  const classes = [styles.picker, className].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      {label && <span className={styles.label}>{label}</span>}

      <div className={styles.controls}>
        {/* Native color input (visual swatch) */}
        <button
          type="button"
          className={styles.swatchButton}
          style={{ backgroundColor: value }}
          onClick={() => nativeRef.current?.click()}
          aria-label="Choose color"
        >
          <input
            ref={nativeRef}
            type="color"
            value={value}
            onChange={handleNativeChange}
            className={styles.nativeInput}
            tabIndex={-1}
          />
        </button>

        {/* Hex text input */}
        <input
          type="text"
          value={inputValue}
          onChange={handleHexInput}
          className={styles.hexInput}
          placeholder="#7c3aed"
          maxLength={7}
          spellCheck={false}
          aria-label="Hex color code"
        />
      </div>

      {/* Preset swatches */}
      {presets.length > 0 && (
        <div className={styles.presets} role="listbox" aria-label="Color presets">
          {presets.map((color) => (
            <button
              key={color}
              type="button"
              role="option"
              aria-selected={value.toLowerCase() === color.toLowerCase()}
              className={[
                styles.preset,
                value.toLowerCase() === color.toLowerCase() && styles.presetActive,
              ].filter(Boolean).join(' ')}
              style={{ backgroundColor: color }}
              onClick={() => handlePresetClick(color)}
              aria-label={color}
            />
          ))}
        </div>
      )}
    </div>
  )
}
